"use client"

import { ArrowRight, CheckCircle2 } from 'lucide-react'

import { getAngiAffiliateUrl } from '@/data/angi-affiliate'
import type { ServiceQuoteFunnel } from '@/data/service-quote-funnels'
import { trackEvent } from '@/lib/analytics'
import { useServiceLeadCtaViewed } from '@/hooks/use-service-lead-cta-viewed'

interface RailAngiCardProps {
  funnel: ServiceQuoteFunnel
  slug: string
}

/**
 * Compact sidebar Angi referral for hire-intent blog posts (blog rail v2,
 * Variant C). On "best lawn care services in {city}" posts the reader is
 * shopping for a pro, not a fertilizer — the rail's one conversion card
 * matches that intent instead of showing Amazon products. Same CJ-tracked
 * destination as the in-body service-quote card; distinct sid/placement so
 * the two surfaces stay separable in CJ and PostHog.
 */
export function RailAngiCard({ funnel, slug }: RailAngiCardProps) {
  const angiUrl = getAngiAffiliateUrl(funnel.serviceType, 'blog_sidebar_service_quote')
  const viewRef = useServiceLeadCtaViewed({
    service_type: funnel.serviceType,
    source_page: `/blog/${slug}`,
    source_placement: 'blog_sidebar',
  })

  return (
    <div
      ref={viewRef}
      className="p-5 rounded-[18px] bg-[rgba(10,28,18,0.34)] backdrop-blur-xl border border-white/20"
      style={{ boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.30)' }}
    >
      <p className="text-[11px] font-bold uppercase tracking-[0.12em] text-green-300">
        Free local quotes
      </p>
      <p className="font-editorial text-white font-semibold text-base mt-1">
        Get matched with {funnel.label.toLowerCase()} pros
      </p>
      <ul className="mt-2.5 space-y-1.5">
        {['Free to request, no obligation', 'Local, background-checked pros', 'Compare multiple quotes'].map((item) => (
          <li key={item} className="flex items-start gap-2 text-xs leading-5 text-white/75">
            <CheckCircle2 className="mt-0.5 h-3.5 w-3.5 shrink-0 text-green-400" aria-hidden="true" />
            {item}
          </li>
        ))}
      </ul>
      <a
        href={angiUrl}
        target="_blank"
        rel="sponsored noopener noreferrer"
        className="mt-3.5 inline-flex w-full items-center justify-center gap-2 rounded-full bg-linear-to-r from-green-600 to-emerald-600 px-6 min-h-[44px] text-white text-sm font-semibold transition hover:-translate-y-0.5 shadow-[0_12px_26px_-8px_rgba(22,163,74,0.5)]"
        onClick={() =>
          trackEvent('service_lead_angi_clicked', {
            service_type: funnel.serviceType,
            source_page: `/blog/${slug}`,
            source_placement: 'blog_sidebar',
            placement: 'blog_sidebar',
          })
        }
      >
        {funnel.ctaLabel}
        <ArrowRight className="h-4 w-4" aria-hidden="true" />
      </a>
      <p className="mt-2.5 text-[11px] leading-4 text-white/55">
        Angi is a partner; we may earn a commission if you request quotes there.
      </p>
    </div>
  )
}
